import { useMemo, useRef } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import { AnimatePresence, motion } from 'motion/react';
import { ProjectSelectInline } from '@/entities/project';
import {
  ARTIFACT_KIND_FILTER_CHOICES,
  PickerTable,
  resolveWorkspaceArtifactSort,
  usePickerSelection,
  WORKSPACE_ARTIFACT_SORT_CHOICES,
  type WorkspaceArtifactRow,
} from '@/entities/artifact';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SortChipPopover } from '@/shared/ui/SortChipPopover';
import { useProjectContextRows } from '../../api/useProjectContextRows';
import type { ProjectContextPickerProps } from './ProjectContextPicker.types';
import { useTranslation } from 'react-i18next';

/** Detail map is empty here: the project picker falls back to palette icons. */
const EMPTY_DETAILS = new Map<string, Record<string, unknown>>();

/** No "already attached" concept in this flow — stable empty set per render. */
const EMPTY_ATTACHED_IDS = new Set<string>();

/** PickerTable keys rows by id; the merged list has no version dimension. */
function getRowDetailKey(row: WorkspaceArtifactRow): string {
  return row.id;
}

function matchesSearch(row: WorkspaceArtifactRow, search: string): boolean {
  if (!search) return true;
  const needle = search.toLowerCase();
  return (
    row.name.toLowerCase().includes(needle) ||
    (row.contextName?.toLowerCase().includes(needle) ?? false)
  );
}

/**
 * Sorts the merged rows client-side. A `null` sort id (and the "newest first"
 * reset) keeps the picker's default order: `createdAt` DESC, ties broken by
 * lowest id (matching the rows hook's server-side default). Selecting an
 * explicit option re-sorts by name or oldest-first.
 */
function sortRows(rows: WorkspaceArtifactRow[], sortId: string | null): WorkspaceArtifactRow[] {
  const resolved = resolveWorkspaceArtifactSort(sortId);
  const sortBy = resolved?.sortBy ?? 'createdAt';
  const direction = resolved?.sortOrder ?? 'DESC';

  return [...rows].sort((a, b) => {
    if (sortBy === 'name') {
      const byName = a.name.localeCompare(b.name);
      const ordered = direction === 'ASC' ? byName : -byName;
      if (ordered !== 0) return ordered;
    } else if (a.createdAt !== b.createdAt) {
      const olderFirst = a.createdAt < b.createdAt ? -1 : 1;
      return direction === 'ASC' ? olderFirst : -olderFirst;
    }
    // Stable tiebreak by lowest id, matching the server-side default.
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * Single-screen "Add Context From Project" picker.
 *
 * A control row — inline project selector + Sort chip + Type filter + search —
 * sits above the project's merged Briefs + Library Artifacts table. The shell
 * reuses `PickerTable` + `usePickerSelection` (and the shared chip atoms) so the
 * visuals match the workspace picker without depending on the search-on-left
 * `PickerToolbar` layout the workspace hosts rely on.
 *
 * Fully controlled: the host owns the selection + control-row state. Type
 * filter, search, and sort apply client-side over the merged list (small
 * per-project sets; one round-trip per source). No project is selected on open;
 * the table shows a "choose a project" zero-state until the user picks one.
 */
export function ProjectContextPicker(props: ProjectContextPickerProps) {
  const { t } = useTranslation('projects');

  const { rows, isLoading } = useProjectContextRows(props.selectedProjectId ?? undefined);

  const debouncedSearch = useDebounce(props.searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim().toLowerCase();

  const visibleRows = useMemo(() => {
    const filtered = rows.filter(
      (row) =>
        (props.kindFilter === null || row.kind === props.kindFilter) &&
        matchesSearch(row, trimmedSearch),
    );
    return sortRows(filtered, props.sort);
  }, [rows, props.kindFilter, props.sort, trimmedSearch]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const selection = usePickerSelection({
    rows: visibleRows,
    selectedIds: props.selectedIds,
    attachedIds: EMPTY_ATTACHED_IDS,
    onSelectedIdsChange: props.onSelectedIdsChange,
  });

  const hasFilter = props.kindFilter !== null || trimmedSearch.length > 0;

  // The table + its controls only make sense once a project is chosen, so the
  // picker opens with just the project selector and reveals the rest (animated)
  // after a project is selected.
  const hasProject = Boolean(props.selectedProjectId);

  // Remount the table (and reset the shift-click anchor) when the list reshapes.
  const tableResetKey = `${props.selectedProjectId ?? ''}-${trimmedSearch}-${props.kindFilter ?? ''}-${props.sort ?? ''}`;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      {/* Control row: project selector always; sort · Type · search reveal once a project is chosen. */}
      <div className="flex items-center gap-2">
        <ProjectSelectInline
          workspaceId={props.workspaceId}
          selectedProjectId={props.selectedProjectId}
          onProjectSelected={props.onSelectedProjectIdChange}
          searchInput={props.projectSearchInput}
          onSearchInputChange={props.onProjectSearchInputChange}
        />

        <AnimatePresence>
          {hasProject && (
            <motion.div
              key="picker-controls"
              className="flex flex-1 items-center gap-2"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <SortChipPopover
                options={WORKSPACE_ARTIFACT_SORT_CHOICES}
                value={props.sort}
                onChange={props.onSortChange}
                resetOptionLabel={t('addProjectContext.projectContextPicker.newestFirst')}
              />

              <FilterChipPopover<ArtifactKind>
                label={t('addProjectContext.projectContextPicker.type')}
                value={props.kindFilter}
                onChange={props.onKindFilterChange}
                options={ARTIFACT_KIND_FILTER_CHOICES}
                triggerAriaLabel={t('addProjectContext.projectContextPicker.filterByType')}
                resetOptionLabel={t('addProjectContext.projectContextPicker.allTypes')}
              />

              <SearchInput
                placeholder={t('addProjectContext.projectContextPicker.search')}
                value={props.searchInput}
                onChange={props.onSearchInputChange}
                aria-label={t('addProjectContext.projectContextPicker.searchBriefsAndArtifacts')}
                className="ml-auto w-80"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        {hasProject ? (
          <motion.div
            key="picker-table"
            className="flex min-h-0 flex-1 flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border **:data-[slot=datatable-container]:overflow-visible"
              data-testid="project-context-picker-scroll-region"
            >
              <PickerTable
                key={tableResetKey}
                rows={visibleRows}
                isLoading={isLoading}
                hasFilter={hasFilter}
                selectedIds={props.selectedIds}
                attachedIds={EMPTY_ATTACHED_IDS}
                scrollRef={scrollRef}
                dataByRowKey={EMPTY_DETAILS}
                getRowDetailKey={getRowDetailKey}
                emptyMessage={t('addProjectContext.projectContextPicker.noBriefsOrArtifactsIn')}
                selectableIdCount={selection.selectableIdCount}
                allSelectableSelected={selection.allSelectableSelected}
                someSelectableSelected={selection.someSelectableSelected}
                onToggleSelectAll={selection.onToggleSelectAll}
                onRowActivate={selection.onRowActivate}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="picker-empty"
            className="flex min-h-0 flex-1 items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="project-context-picker-empty"
          >
            <p className="text-sm text-muted-foreground">
              {t('addProjectContext.projectContextPicker.chooseAProjectToSee')}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
